import { FC } from "react";
import { Study } from "../../types/Study";
import { format } from "date-fns";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface PaperDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  study: Study | null;
}

const PaperDetailsModal: FC<PaperDetailsModalProps> = ({
  isOpen,
  onClose,
  study,
}) => {
  if (!isOpen || !study) return null;

  const years = study.citations_by_year.map((item) => item.year);
  const citationCounts = study.citations_by_year.map(
    (item) => item.cited_by_count
  );

  const data = {
    labels: years,
    datasets: [
      {
        label: "Citations per Year",
        data: citationCounts,
        backgroundColor: "rgba(74, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  };

  const options = {
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Citations",
        },
      },
      x: {
        title: {
          display: true,
          text: "Year",
        },
      },
    },
  };

  const handleBackdropClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg w-11/12 max-w-3xl h-5/6 p-6 shadow-lg relative overflow-y-auto">
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl font-bold"
          onClick={onClose}
        >
          &times;
        </button>

        <h2 className="text-2xl font-bold text-gray-900 mb-3 me-3">
          {study.title}
        </h2>

        <p className="text-blue-500 mb-2">
          <span className="font-semibold text-gray-900 ">Authors:</span>{" "}
          {study.authors?.join(", ") || "Unknown Authors"}
        </p>

        <p className="text-blue-500 mb-2">
          <span className="font-semibold text-gray-900 ">Published:</span>{" "}
          {study.publication_date
            ? format(new Date(study.publication_date), "do MMMM yyyy")
            : "Unknown Date"}
        </p>

        <p className="text-blue-500 mb-2">
          <span className="font-semibold text-gray-900 ">Source:</span>{" "}
          {study.source}
        </p>

        {study.doi && (
          <button className="text-white bg-blue-500  hover:ring-2 hover:ring-opacity-50 px-4 py-1 my-3 rounded-md">
            <a href={study.doi} target="_blank" rel="noopener noreferrer">
              View DOI
            </a>
          </button>
        )}

        <p className="font-semibold text-gray-800 mb-1">Description:</p>

        {study.description && (
          <p className="text-gray-700 text-justify mb-3 font-normal">
            {study.description}
          </p>
        )}

        <p className="text-blue-500 mb-2">
          <span className="font-semibold text-gray-900 ">Total Citations:</span>{" "}
          {study.citations_count}
        </p>

        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Citations per Year:
          </h3>
          <Bar data={data} options={options} />
        </div>
      </div>
    </div>
  );
};

export default PaperDetailsModal;
